import {
  TECHNICAL_FILTERS,
  formatTechnicalValue,
  parseRangeQuery,
  technicalFilterScale,
} from './technicalFilters.js'

export const BULBS_FETCH_LIMIT = 50

export const MIN_BULBS_FOR_FILTERS = 3

export const TEASER_MIN_RATIO = 1.2

export const TEASER_MIN_TINTS = 2

export const BULB_BADGES = ['strongest', 'best_value']

export const BULB_FILTER_ORDER = ['lumens', 'watts', 'color_temperature']

const technicalValue = (bulb, key) => {
  const raw = bulb?.variant?.[key]
  if (raw === null || raw === undefined || raw === '') return null

  const value = Number(raw)

  return Number.isFinite(value) && value > 0 ? value : null
}

const activeRanges = query =>
  TECHNICAL_FILTERS.map(filter => [
    filter,
    parseRangeQuery(query?.[filter.query]),
  ]).filter(([, range]) => range)

const withinRange = (value, [low, high]) =>
  value !== null &&
  (low === null || value >= low) &&
  (high === null || value <= high)

export function bulbTechnicalRanges(bulbs) {
  return Object.fromEntries(
    TECHNICAL_FILTERS.map(filter => {
      const values = [
        ...new Set(
          (bulbs || [])
            .map(bulb => technicalValue(bulb, filter.key))
            .filter(value => value !== null)
        ),
      ].sort((a, b) => a - b)

      return values.length
        ? [
            filter.key,
            { min: values[0], max: values[values.length - 1], values },
          ]
        : null
    }).filter(Boolean)
  )
}

export function bulbFiltersAvailable(bulbs, ranges) {
  if ((bulbs || []).length < MIN_BULBS_FOR_FILTERS) return false

  return TECHNICAL_FILTERS.some(
    filter => technicalFilterScale(ranges, filter).bounds
  )
}

export function hasActiveBulbFilters(query) {
  return activeRanges(query).length > 0
}

export function filterBulbs(bulbs, query) {
  const active = activeRanges(query)
  const list = bulbs || []
  if (!active.length) return list

  return list.filter(bulb =>
    active.every(([filter, range]) =>
      withinRange(technicalValue(bulb, filter.key), range)
    )
  )
}

export function bulbsWithoutData(bulbs, query) {
  const active = activeRanges(query)
  if (!active.length) return 0

  return (bulbs || []).filter(bulb =>
    active.some(([filter]) => technicalValue(bulb, filter.key) === null)
  ).length
}

const productId = value => {
  const id = Number(value)

  return Number.isInteger(id) && id > 0 ? id : null
}

export function listBadges(meta) {
  const badges = meta?.badges || {}

  return {
    strongest: productId(badges.strongest),
    best_value: productId(badges.best_value),
  }
}

export function bulbBadge(badges, id) {
  const target = productId(id)
  if (!badges || target === null) return null

  return BULB_BADGES.find(key => badges[key] === target) || null
}

export function bulbsTeaser(teaser) {
  const ratio = Number(teaser?.brightest_ratio)
  const tints = Number(teaser?.tints_count)

  return {
    ratio:
      Number.isFinite(ratio) && ratio >= TEASER_MIN_RATIO
        ? Math.round(ratio * 10) / 10
        : null,
    tints: Number.isInteger(tints) && tints >= TEASER_MIN_TINTS ? tints : null,
  }
}

export function formatTeaserRatio(ratio, locale) {
  return ratio.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
}

export function bulbSpecs(variant, locale) {
  return BULB_FILTER_ORDER.map(key => {
    const value = technicalValue({ variant }, key)
    if (value === null) return null

    const filter = TECHNICAL_FILTERS.find(item => item.key === key)

    return {
      key,
      text: formatTechnicalValue(value, filter, locale),
      kelvin: key === 'color_temperature' ? value : null,
    }
  }).filter(Boolean)
}
